import React, { ReactElement, useEffect, useState } from 'react';
import {
  Box,
  Grid,
  Paper,
  Typography,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  CircularProgress,
  IconButton,
} from '@mui/material';
import { Formik, Form } from 'formik';
import AddIcon from '@mui/icons-material/Add';
import RefreshIcon from '@mui/icons-material/Refresh';
import {
  AppFormikTextField,
  AppPrimaryButton,
  ConfirmDialog,
} from 'shared/components/AppComponents';
import { SBOMValidationSchema } from 'validators/SBOMValidationSchema';
import DeleteIcon from '@mui/icons-material/Delete';
import { AISystemsService } from 'services/AISystemService';
import { useParams } from 'react-router-dom';
import { Toast, Utils } from 'shared/helpers';
import { BaseApplicationState } from 'state/baseapplication/Reducer';
import { useSelector } from 'react-redux';

const AgenticToolsSBOM = (): ReactElement => {
  const [sbomData, setSbomData] = useState<any>([]);
  const [openDeleteSBOM, setOpenDeleteSBOM] = useState<any>(null);
  const [isLoading, setIsLoading] = useState<any>(true);

  const { aiSystemId } = useParams();
  const { orgACLConfig }: BaseApplicationState = useSelector(
    (state: any) => state.baseAppState
  );
  const handleGetSBOM = (): void => {
    AISystemsService.getSBOM(aiSystemId)
      .then((res: any) => {
        setSbomData(res.data.data);
        setIsLoading(false);
      })
      .catch((error: any) => {
        Utils.handleErrorResponse(error);
      });
  };

  const handleCreateSBOM = (data: any, formik: any): void => {
    AISystemsService.createSBOM({ ...data, ai_system_id: aiSystemId })
      .then((response: any) => {
        formik.resetForm();
        Utils.handleSuccessResponse(response);
        handleGetSBOM();
      })
      .catch((error: any) => {
        Utils.handleErrorResponse(error);
      });
  };

  const handleCloseDeleteSBOM = (): void => {
    setOpenDeleteSBOM(null);
  };

  const handleDeleteSBOM = (id: any): void => {
    AISystemsService.deleteSBOM(id)
      .then((res) => {
        handleGetSBOM();
        Utils.handleSuccessResponse(res);
        setOpenDeleteSBOM(null);
      })
      .catch((e) => {
        Toast.showErrorMessage(e);
      });
  };

  useEffect(() => {
    handleGetSBOM();
  }, []);

  return (
    <>
      <Formik
        initialValues={{
          author_contacts: '',
          component_id: '',
          license_terms: '',
          name: '',
          sha256_hash: '',
          source_url: '',
          supplier: '',
          version: '',
        }}
        validationSchema={SBOMValidationSchema}
        onSubmit={(formValues, formik) => {
          handleCreateSBOM(formValues, formik);
        }}
      >
        {({ handleSubmit, isValid, dirty }) => (
          <Form onSubmit={handleSubmit}>
            <Box className="paper-form-content">
              <Paper variant="outlined">
                <Box
                  className="flex items-center space-between"
                  sx={{
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'space-between',
                    p: 2,
                  }}
                >
                  <Typography variant="h3" sx={{ fontWeight: 600 }}>
                    Agentic Tools and Software Bill of Materials (SBOM)
                  </Typography>
                  {orgACLConfig.aiInventory?.includes(
                    'aiInventory.aiSystem.aiInventory.view.sbom.discoverTools'
                  ) && (
                    <AppPrimaryButton
                      startIcon={<RefreshIcon />}
                      type="button"
                      className="btn-small"
                      title="Discover Tools"
                    />
                  )}
                </Box>

                <Box className="paper-form-input-content" sx={{ p: 3 }}>
                  <Grid container spacing={3}>
                    <Grid item xs={2}>
                      <AppFormikTextField
                        name="component_id"
                        placeholder="Component ID"
                      />
                    </Grid>

                    <Grid item xs={2}>
                      <AppFormikTextField name="name" placeholder="Name" />
                    </Grid>

                    <Grid item xs={1.5}>
                      <AppFormikTextField
                        name="version"
                        placeholder="Version"
                      />
                    </Grid>

                    <Grid item xs={2}>
                      <AppFormikTextField
                        name="supplier"
                        placeholder="Supplier"
                      />
                    </Grid>

                    <Grid item xs={2.5}>
                      <AppFormikTextField
                        name="sha256_hash"
                        placeholder="SHA256 Hash"
                      />
                    </Grid>

                    <Grid item xs={2}>
                      <AppFormikTextField
                        name="source_url"
                        placeholder="Source URL"
                      />
                    </Grid>

                    <Grid item xs={2}>
                      <AppFormikTextField
                        name="license_terms"
                        placeholder="License Terms"
                      />
                    </Grid>

                    <Grid item xs={2}>
                      <AppFormikTextField
                        name="author_contacts"
                        placeholder="Author Contacts"
                      />
                    </Grid>

                    <Grid item xs={2} display="flex" alignItems="center">
                      {orgACLConfig.aiInventory?.includes(
                        'aiInventory.aiSystem.aiInventory.view.sbom.addAgenticTools'
                      ) && (
                        <AppPrimaryButton
                          startIcon={<AddIcon />}
                          type="submit"
                          title="Add Item"
                          onClick={handleSubmit}
                          disabled={!isValid || !dirty}
                        />
                      )}
                    </Grid>
                  </Grid>
                </Box>

                {isLoading && (
                  <Box className="empty-content">
                    <CircularProgress />
                  </Box>
                )}
                {!isLoading && (
                  <TableContainer component={Paper}>
                    <Table
                      sx={{ minWidth: 650 }}
                      className="app-table p-3"
                      aria-label="simple table"
                    >
                      <TableHead>
                        <TableRow>
                          <TableCell>License Terms</TableCell>
                          <TableCell>Component Id</TableCell>
                          <TableCell>Name</TableCell>
                          <TableCell>Version</TableCell>
                          <TableCell>Supplier</TableCell>
                          <TableCell>Source url</TableCell>
                          <TableCell>Author (s)</TableCell>
                          <TableCell>&nbsp;</TableCell>
                        </TableRow>
                      </TableHead>

                      <TableBody>
                        {sbomData.length > 0
                          ? sbomData.map((row: any) => (
                              <TableRow
                                key={row.id}
                                sx={{
                                  '&:last-child td, &:last-child th': {
                                    border: 0,
                                  },
                                }}
                              >
                                <TableCell component="th" scope="row">
                                  {row?.license_terms
                                    ? row?.license_terms
                                    : '-'}
                                </TableCell>
                                <TableCell component="th" scope="row">
                                  {row?.component_id}
                                </TableCell>
                                <TableCell>{row?.name}</TableCell>
                                <TableCell component="th" scope="row">
                                  {row?.version}
                                </TableCell>
                                <TableCell component="th" scope="row">
                                  {row?.supplier}
                                </TableCell>
                                <TableCell component="th" scope="row">
                                  {row?.source_url}
                                </TableCell>
                                <TableCell component="th" scope="row">
                                  {row?.author_contacts}
                                </TableCell>
                                <TableCell>
                                  {orgACLConfig.aiInventory?.includes(
                                    'aiInventory.aiSystem.aiInventory.view.sbom.deleteAgenticTool'
                                  ) && (
                                    <IconButton
                                      onClick={() =>
                                        setOpenDeleteSBOM(row?._id)
                                      }
                                      color="error"
                                      size="small"
                                      aria-label="delete"
                                    >
                                      <DeleteIcon />
                                    </IconButton>
                                  )}
                                </TableCell>
                              </TableRow>
                            ))
                          : !isLoading && (
                              <Typography variant="body2" color="#7e7e7e">
                                No SBOM items have been added yet.
                              </Typography>
                            )}
                      </TableBody>
                    </Table>
                  </TableContainer>
                )}
              </Paper>
            </Box>
          </Form>
        )}
      </Formik>
      <ConfirmDialog
        title="Delete Tool"
        subTitle="Are you sure to delete the tool?"
        showConfirm={openDeleteSBOM !== null}
        cancelTitle="Close"
        successTitle="Delete"
        onSuccess={() => {
          handleCloseDeleteSBOM();
          handleDeleteSBOM(openDeleteSBOM);
        }}
        onCancel={handleCloseDeleteSBOM}
        onCloseDialog={handleCloseDeleteSBOM}
      />
    </>
  );
};

export default AgenticToolsSBOM;
